<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.customer.actions.image.title')"
    :info="customer ? customer.displayName : $t('general.customer')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      {
        text: $t('components.customer.actions.image.remove'),
        type: 'confirm',
        variant: 'danger',
        callback: handleRemove,
        validate: false
      },
      {
        text: $t('general.confirm'),
        type: 'confirm',
        callback: handleConfirm,
        disabled: !selected && !image
      }
    ]"
    :portal="portal"
    :valid="validate"
    :loading="loading || uploading"
  >
    <div
      id="scroll-container"
      class="mt-4 text-left "
    >
      <o-scroll class="overflow-visible">
        <o-pager
          v-if="images"
          :button="$t('general.loadMore')"
          :empty="$t('asset.noPhotos')"
          :list="images"
        >
          <template #default="{ data }">
            <div class="flex flex-wrap">
              <div
                v-for="(photo, key) in data"
                :key="key"
                class="h-[6.6rem] w-[6.6rem] m-2 relative"
              >
                <img
                  :src="photo.imageUrls.medium"
                  :alt="photo.caption"
                  class="object-cover h-full w-full bg-white rounded-lg hover:scale-101 transition-transform cursor-pointer"
                  :class="{
                    'ring ring-primary-500 ring-offset-1':
                      selected && photo.idPhoto === selected.idPhoto
                  }"
                  @click="handleSelectPhoto(photo)"
                >

                <div
                  v-if="selected && photo.idPhoto === selected.idPhoto"
                  class="w-8 h-8 rounded-full absolute -top-4 -right-4 bg-primary-400 flex items-center justify-center border-2 border-white"
                >
                  <o-icon icon="check" class="text-white" fill />
                </div>
              </div>
            </div>
          </template>
        </o-pager>

        <o-form v-if="!uploading" ref="form">
          <o-field-group :label="$t('general.image')" :name="$t('general.image')" class="block pb-3">
            <o-upload v-model="image" />
          </o-field-group>
        </o-form>
      </o-scroll>
    </div>

    <template #loading>
      <div v-if="uploading" class="w-full max-w-sm mx-auto">
        <o-progress v-if="progress" :progress="progress" :label="$t('general.uploading')" />
        <o-loader v-else class="mx-auto" />
      </div>
    </template>
  </o-dialog>
</template>

<script setup lang="ts">
import { EndlessListPager } from '@/_api/lib/pager'
import { OPager, OUpload, OProgress } from '@/components/elements'

const { $api, $notification, $i18n } = useNuxtApp()

interface Props {
  portal?: string
}

withDefaults(defineProps<Props>(), {
  portal: undefined,
})

const emit = defineEmits<{
  (e: 'success', customer: any): void
  (e: 'update', customer: any): void
}>()

const dialog = ref<any>()
const form = ref<any>()

const customer = ref<any>(null)
const images = ref<any>(null)
const selected = ref<any>(null)
const loading = ref(false)
const image = ref<any>(null)
const uploading = ref(false)
const progress = ref(0)

const upload = computed({
  get () {
    if (image.value instanceof File) {
      return image.value
    }

    return null
  },
  set (value: any) {
    image.value = value
  },
})

watch(image, (newVal) => {
  if (newVal) {
    selected.value = null
  }
})

function open (customerData: any, selectedData?: any) {
  customer.value = customerData
  selected.value = selectedData
  loadPhotos()

  dialog.value.open()
}

function loadPhotos () {
  images.value = new EndlessListPager(
    (page: number) => $api.customer.getPhotos(String(customer.value.idCustomer)),
    12,
  )

  images.value.load()
}

function validate () {
  return !!selected.value || !!image.value
}

function handleSelectPhoto (photo: any) {
  image.value = null
  selected.value = photo
}

async function handleUpload () {
  uploading.value = true

  try {
    const img = upload.value
    const options = {
      onUploadProgress: ($event: any) => handleOnUploadProgress($event),
    }

    const response = await $api.customer.addPhoto(
      String(customer.value.idCustomer),
      { photo: img!, caption: img!.name },
      options.onUploadProgress,
    )
    const photo = response.data

    if (photo) {
      image.value = null
      uploading.value = false
      progress.value = 0

      return photo
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
    image.value = null
  }

  uploading.value = false
}

async function handleConfirm () {
  loading.value = true

  try {
    let photo

    if (image.value) {
      photo = await handleUpload()
    } else {
      photo = selected.value
    }

    const profileResponse = await $api.customer.setProfilePhoto(String(customer.value.idCustomer), String(photo.idPhoto))
    const result = profileResponse.data

    if (result) {
      $notification.toast({
        title: $i18n.t('components.customer.actions.image.success'),
        text: $i18n.t('asset.successInfo', { name: customer.value.displayName }),
        variant: 'success',
      })

      emit('success', result)
      emit('update', result)

      loading.value = false

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

async function handleRemove () {
  loading.value = true

  try {
    const removeResponse = await $api.customer.removeProfilePhoto(String(customer.value.idCustomer))
    const result = removeResponse.data

    if (result) {
      $notification.toast({
        title: $i18n.t('components.customer.actions.image.removeSuccess'),
        text: $i18n.t('components.customer.actions.image.removeSuccessInfo', { name: customer.value.displayName }),
        variant: 'success',
      })

      emit('success', result)
      emit('update', result)

      loading.value = false

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

function handleOnUploadProgress ($event: any) {
  progress.value = parseInt(String(Math.round(($event.loaded / $event.total) * 100)))
}

defineExpose({ open })
</script>

<style lang="scss">
#scroll-container {
  .os-viewport {
    overflow: visible;
  }
}
</style>
